import React, { createContext, useContext, useEffect, useState } from "react";
import {
  getAccessToken,
  subscribeAccessToken,
  refreshAccessToken,
} from "../api/apiClient";

const AuthContext = createContext();
const SESSION_RECOVERY_GRACE_MS = 3000;
const SESSION_RECOVERY_RETRY_MS = 500;

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const AuthProvider = ({ children }) => {
  const [isAuth, setIsAuth] = useState(!!getAccessToken());
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null); // { username, email, role }

  useEffect(() => {
    // update whenever the in-memory token changes
    const unsubscribe = subscribeAccessToken((token) => {
      setIsAuth(!!token);
      if (!token) {
        setUser(null); // Clear user data on logout
      }
    });

    // ensure initial check (in case token set after mount)
    (async () => {
      try {
        // Try to recover session for up to 3 seconds before considering user logged out.
        const startedAt = Date.now();
        let recovered = false;

        while (
          !recovered &&
          Date.now() - startedAt < SESSION_RECOVERY_GRACE_MS
        ) {
          try {
            const data = await refreshAccessToken();
            setIsAuth(!!data.accessToken);
            setUser(data.user);
            recovered = true;
          } catch {
            const elapsed = Date.now() - startedAt;
            if (elapsed >= SESSION_RECOVERY_GRACE_MS) {
              break;
            }

            await wait(SESSION_RECOVERY_RETRY_MS);
          }
        }

        if (!recovered) {
          setIsAuth(false);
          setUser(null);
        }
      } catch (e) {
        setIsAuth(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ isAuth, setIsAuth, loading, user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
